import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';

interface Reaction {
  id: string;
  name: string;
  emoji: string;
  animation_class: string;
  points: number;
}

interface PostReaction {
  id: string;
  reaction_id: string;
  user_id: string;
}

interface PostReactionsProps {
  postId: string;
  className?: string;
}

export const PostReactions: FC<PostReactionsProps> = ({ postId, className }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showReactionPicker, setShowReactionPicker] = useState(false);

  // Si no hay postId, no renderizar nada
  if (!postId) {
    return null;
  }

  // Obtener todas las reacciones disponibles
  const { data: availableReactions } = useQuery<Reaction[]>({
    queryKey: ['reactions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_reactions')
        .select('*')
        .order('points', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Obtener reacciones del post actual
  const { data: postReactions } = useQuery<PostReaction[]>({
    queryKey: ['post-reactions', postId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('post_reactions')
        .select('*')
        .eq('post_id', postId);

      if (error) throw error;
      return data || []; // Retornar array vacío si no hay datos
    },
    enabled: !!postId, // Solo ejecutar si hay postId
  });

  // Mutación para agregar reacción
  const addReaction = useMutation({
    mutationFn: async (reactionId: string) => {
      if (!user?.id || !postId) {
        throw new Error('Usuario no autenticado o post inválido');
      }

      const { error } = await supabase
        .from('post_reactions')
        .insert({
          post_id: postId,
          user_id: user.id,
          reaction_id: reactionId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['post-reactions', postId]);
      setShowReactionPicker(false);
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo agregar la reacción",
      });
    },
  });

  // Mutación para eliminar reacción
  const removeReaction = useMutation({
    mutationFn: async (reactionId: string) => {
      if (!user?.id || !postId) {
        throw new Error('Usuario no autenticado o post inválido');
      }

      const { error } = await supabase
        .from('post_reactions')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .eq('reaction_id', reactionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['post-reactions', postId]);
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar la reacción",
      });
    },
  });

  const handleReactionClick = (reactionId: string) => {
    const existingReaction = postReactions?.find(
      r => r.reaction_id === reactionId && r.user_id === user?.id
    );

    if (existingReaction) {
      removeReaction.mutate(reactionId);
    } else {
      addReaction.mutate(reactionId);
    }
  };

  const getReactionCount = (reactionId: string) => {
    return postReactions?.filter(r => r.reaction_id === reactionId).length || 0;
  };

  const hasUserReacted = (reactionId: string) => {
    return postReactions?.some(
      r => r.reaction_id === reactionId && r.user_id === user?.id
    );
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {availableReactions?.map((reaction) => {
        const count = getReactionCount(reaction.id);
        const hasReacted = hasUserReacted(reaction.id);
        
        return (
          <TooltipProvider key={reaction.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={hasReacted ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "flex items-center gap-1 transition-all",
                    reaction.animation_class,
                    hasReacted && "bg-primary/20"
                  )}
                  onClick={() => handleReactionClick(reaction.id)}
                >
                  <span className="text-lg">{reaction.emoji}</span>
                  {count > 0 && (
                    <span className="text-xs font-medium">{count}</span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-white border shadow-lg px-3 py-1.5">
                <p className="text-sm text-gray-700">{reaction.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
};
